"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.matchStringWithWildcards = exports.matchCondition = exports.evaluateActionAgainstRoles = exports.matchActionToGrants = exports.matchGrant = void 0;
const glob_json_path_1 = require("glob-json-path");
const lodash_1 = require("lodash");
const utils_1 = require("./utils");
const matchGrant = (params) => {
    const { action, resource, entity, supportInputWildcard, grant } = params;
    if (action === grant.action && resource === grant.resource) {
        // Rule 7: - - Conditions are evaluated using the entity passed to the evaluation function.
        return (0, lodash_1.isEmpty)(grant.conditions)
            ? true
            : grant.conditions.every((condition) => (0, exports.matchCondition)({ condition, action, entity }));
    }
    // Rule 6: - - Wildcard expressions are supported
    const isMatch = (0, exports.matchStringWithWildcards)(action, grant.action) &&
        (!resource ||
            !grant.resource ||
            grant.resource === '*' ||
            (0, exports.matchStringWithWildcards)(resource, grant.resource, supportInputWildcard ?? false));
    // if there are conditions make sure to evaluate them too
    if (isMatch && !(0, lodash_1.isEmpty)(grant.conditions)) {
        // Rule 7: - - Conditions are evaluated using the entity passed to the evaluation function.
        return grant.conditions.every((condition) => (0, exports.matchCondition)({ condition, action, entity }));
    }
    return isMatch;
};
exports.matchGrant = matchGrant;
const matchActionToGrants = (params) => {
    const { grants } = params;
    // Rule 6: An evaluation will try to match all available grants where a given action and resource matches.
    const matches = grants.filter((grant) => (0, exports.matchGrant)({ grant, ...params }));
    // Rule 5: Neither must contain a matched explicit deny.
    if (matches.some((m) => m.effect === 'deny')) {
        return false;
    }
    return matches.length > 0;
};
exports.matchActionToGrants = matchActionToGrants;
const evaluateActionAgainstRoles = ({ roles, action, resource, entity, supportInputWildcard = false, }) => {
    if (!roles.length) {
        (0, utils_1.debug)('There are no roles to evaluate, Denying the request by default');
        return false;
    }
    // Rule 5: At least one organization role grant and at least one user role grant in the same organization must be matched to pass the evaluation.
    const orgRoles = roles.filter((r) => r.type === 'org_role');
    (0, utils_1.debug)('all roles %s', JSON.stringify(roles));
    return orgRoles.some((orgRole) => {
        const nonOrgRoles = roles.filter((r) => r.type !== 'org_role' && r.organization_id === orgRole.organization_id);
        const nonOrgGrants = nonOrgRoles.flatMap((r) => r.grants);
        const nonOrgMatches = (0, exports.matchActionToGrants)({ grants: nonOrgGrants, action, resource, entity, supportInputWildcard });
        const orgMatches = (0, exports.matchActionToGrants)({ grants: orgRole.grants, action, resource, entity, supportInputWildcard });
        const isPermitted = nonOrgMatches && orgMatches;
        (0, utils_1.debug)('Evaluating roles against action %s', JSON.stringify({ isPermitted, action, resource, orgRole, nonOrgRoles, entity, supportInputWildcard }));
        return isPermitted;
    });
};
exports.evaluateActionAgainstRoles = evaluateActionAgainstRoles;
const toRegex = (str) => new RegExp('^' +
    str
        // eslint-disable-next-line no-useless-escape
        .replace(/[\-\[\]\/\{\}\(\)\+\.\\\^\$\|]/g, '\\$&')
        .replace(/\*/g, '.*')
        .replace(/\?/g, '.') +
    '$');
const isRegexEqual = (a, b) => {
    // if A or B are empty and one of them is * or ?, then true or else false
    if (!a || !b)
        return (a === '*' || a === '?' ? '' : a) === (b === '*' || b === '?' ? '' : b);
    // split A and B into a head (=first char) and a tail (the rest)
    const [headA, tailA] = [a[0], a.slice(1)];
    const [headB, tailB] = [b[0], b.slice(1)];
    // if heads are equal, then compare both tails
    if (headA === headB || headA === '?' || headB === '?')
        return isRegexEqual(tailA, tailB);
    // if A's head is *, then try A's tail against B, then B.slice(1), then B.slice(2) etc...
    if (headA === '*') {
        for (let i = 0; i <= b.length; i++)
            if (isRegexEqual(tailA, b.slice(i)))
                return true;
        return false;
    }
    // if B's head is *, swap A and B
    if (headB === '*')
        return isRegexEqual(b, a);
    // otherwise, false
    return false;
};
const matchCondition = (params) => {
    if ((0, lodash_1.isEmpty)(params.entity)) {
        return false;
    }
    if (params.condition.operation === 'equals') {
        const { entity, condition } = params;
        const matchedValues = (0, glob_json_path_1.globValues)(condition.attribute, entity);
        const flattenValues = matchedValues.flat();
        // when dealing with arrays, at least a single value found matches the condition values
        return (0, lodash_1.intersection)(condition.values, flattenValues).length > 0;
    }
    return false;
};
exports.matchCondition = matchCondition;
const matchStringWithWildcards = (stringToMatch, stringWithWildcards, supportInputWildcard = false) => {
    if (!stringToMatch)
        return false;
    // special case: resource separator wildcard :*
    // e.g. meter:* should match with just meter
    // this is just a convenience to avoid having to define meter:* and meter grants separately
    if (stringWithWildcards.endsWith(':*')) {
        const stringWithoutSeparator = stringWithWildcards.slice(0, stringWithWildcards.length - 2);
        if (stringToMatch === stringWithoutSeparator) {
            return true;
        }
    }
    if (!supportInputWildcard) {
        return toRegex(stringWithWildcards).test(stringToMatch);
    }
    // contact:Personal Details:* can match with contact:*:first_name or contact:* or contact:Personal Details:first_name
    return isRegexEqual(stringToMatch, stringWithWildcards);
};
exports.matchStringWithWildcards = matchStringWithWildcards;
//# sourceMappingURL=rules.js.map